// Quick-add helpers for the Planner's "New Item" panel.
//
// Pasting a list into the title field offers to add one task per line (like
// Todoist's "Add X tasks?"). This turns pasted text into those titles: one per
// non-empty line, with the list markers people copy along with them removed
// (bullets, "1." / "2)" numbering, checkboxes like "[ ]" / "[x]").

const MARKER = /^\s*(?:[-*•◦▪‣·–—](?:\s+|$)|\d{1,3}[.)]\s+|\[[ xX✓]?\]\s*|[☐☑✓✔]\s*)/;

export function splitPastedLines(text) {
  if (!text) return [];
  return String(text)
    .split(/\r\n|\r|\n/)
    .map(line => {
      let s = line.trim();
      // A bullet and a checkbox can be stacked ("- [ ] milk"), so strip twice.
      for (let i = 0; i < 2; i++) s = s.replace(MARKER, "").trim();
      return s;
    })
    .filter(Boolean);
}

// ── Quick-add bar labels ─────────────────────────────────────────────────────
const pad2 = n => String(n).padStart(2, "0");

// "Today" / "Tomorrow" / "Yesterday", else "Mon, Oct 12" (+ year when not this year).
export function quickDateLabel(dateStr, now = new Date()) {
  if (!dateStr) return "No date";
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  const diff = Math.round((date - new Date(now.getFullYear(), now.getMonth(), now.getDate())) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  const opts = { weekday: "short", month: "short", day: "numeric" };
  if (y !== now.getFullYear()) opts.year = "numeric";
  return date.toLocaleDateString(undefined, opts);
}

// End time for an item given only a start: one hour later, same day ("23:30" → "23:59").
export function oneHourLater(hhmm) {
  const [h, m] = String(hhmm).split(":").map(Number);
  if (!Number.isFinite(h)) return "";
  if (h >= 23) return "23:59";
  return `${pad2(h + 1)}:${pad2(m || 0)}`;
}

// "15:00" → "3:00 PM" (or the phone's own format).
export function fmtQuickTime(hhmm) {
  const [h, m] = String(hhmm).split(":").map(Number);
  if (!Number.isFinite(h)) return "";
  return new Date(2000, 0, 1, h, m || 0).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}


// ── Quick-add date panel ─────────────────────────────────────────────────────
const toYmd = d => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const fromYmd = s => { const [y, m, d] = String(s).split("-").map(Number); return new Date(y, m - 1, d); };

// "2026-10-30" + 3 → "2026-11-02" (local dates, no UTC slip).
export function addDaysStr(dateStr, n) {
  const d = fromYmd(dateStr);
  d.setDate(d.getDate() + n);
  return toYmd(d);
}

// The 42 day cells (6 weeks) shown for a month, starting on the week's first day.
// month is 0-based. Each cell: { date: "YYYY-MM-DD", inMonth }.
export function monthCells(year, month, mondayFirst = false) {
  const first = new Date(year, month, 1);
  const lead = (first.getDay() - (mondayFirst ? 1 : 0) + 7) % 7;
  const start = new Date(year, month, 1 - lead);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    return { date: toYmd(d), inMonth: d.getMonth() === month };
  });
}
